/**
 * Zoom-based province/state visibility configuration.
 *
 * Province borders appear at higher zoom levels to avoid visual clutter
 * at the world view. As zoom increases, more provinces become visible.
 *
 * Natural Earth scalerank: 0 = most important (major states/provinces),
 * higher values = less important.
 *
 * Note: The 50m dataset primarily has provinces with scalerank 2, so
 * provinces start appearing at zoom 6.
 */

import type { ProvinceData } from '../types/features'

/**
 * Minimum scalerank to show at each zoom level.
 * Provinces with scalerank <= threshold will be shown.
 *
 * Note: The 50m Natural Earth dataset has most provinces at scalerank 2,
 * with only a few minor ones at scalerank 5.
 *
 * At zoom < 5: No provinces shown (world view)
 * At zoom 5: Show major provinces (scalerank 0-2)
 * At zoom 6+: Show all provinces
 */
const PROVINCE_SCALERANK_THRESHOLDS: Record<number, number> = {
  5: 2, // Major provinces (most US states, large countries' main divisions)
  6: 10, // All provinces
}

/**
 * Minimum zoom level at which provinces start appearing.
 */
export const PROVINCE_MIN_ZOOM = 5

/**
 * Filter provinces based on zoom level.
 * Returns provinces that should be visible at the given zoom.
 *
 * Returns empty array for zoom < 6 (no provinces at country view).
 */
export function filterProvincesByZoom(
  provinces: ProvinceData[],
  zoom: number
): ProvinceData[] {
  // No provinces below minimum zoom
  if (zoom < PROVINCE_MIN_ZOOM) {
    return []
  }

  // Clamp zoom to our threshold range
  const effectiveZoom = Math.min(Math.floor(zoom), 9)
  const maxScalerank = PROVINCE_SCALERANK_THRESHOLDS[effectiveZoom] ?? 10

  return provinces.filter((province) => province.scalerank <= maxScalerank)
}

/**
 * Get the scalerank threshold for a given zoom level.
 * Returns -1 for zoom levels where no provinces are shown.
 */
export function getProvinceScalerankThreshold(zoom: number): number {
  if (zoom < PROVINCE_MIN_ZOOM) {
    return -1
  }
  const effectiveZoom = Math.min(Math.floor(zoom), 9)
  return PROVINCE_SCALERANK_THRESHOLDS[effectiveZoom] ?? 10
}
